<template>
    <section class="client-breakdown">
        <q-expansion-item
            default-opened
        >
            <template v-slot:header>
                <div class="client-breakdown__eyebrow">Service breakdown: Clients in this selection</div>
            </template>
            <div class="client-breakdown__content">
                <div class="client-breakdown__toggles">
                    <q-toggle
                        v-model="activeClientsOnly"
                        label="Active Clients Only"
                    />
                    <q-toggle
                        v-model="formCompletedOnly"
                        label="Form Completed"
                    />
                </div>

                <q-table
                    class="client-table"
                    flat
                    dense
                    :rows="filteredRows"
                    :columns="columns"
                    row-key="row_key"
                    :loading="loading"
                    :pagination="{ rowsPerPage: 8 }"
                    :rows-per-page-options="[8, 15, 25]"
                    @row-click="(_, row) => $emit('select-client', row)"
                >
                    <template v-slot:body-cell-full_name="props">
                        <q-td :props="props">
                            {{ props.row.full_name }}
                            <q-badge
                                v-if="isTemporaryClientRow(props.row)"
                                color="teal"
                                class="q-ml-sm"
                                label="Internal"
                            />
                        </q-td>
                    </template>

                    <template v-slot:body-cell-latest_submitted_time="props">
                        <q-td :props="props">
                            {{ formatDate(props.row.latest_submitted_time) }}
                        </q-td>
                    </template>

                    <template v-slot:no-data>
                        <div class="q-pa-md text-grey-7">
                            No clients found for this selection.
                        </div>
                    </template>
                </q-table>
            </div>
        </q-expansion-item>
    </section>
</template>

<script>
import { isTemporaryClientRow } from "@/services/util/formSubject";

const columns = [
    { name: 'full_name', label: 'Client', field: 'full_name', align: 'left', sortable: true },
    { name: 'service_type_id', label: 'Service', field: 'service_type_id', align: 'left', sortable: true },
    { name: 'programme_id', label: 'Programme', field: 'programme_id', align: 'left', sortable: true },
    { name: 'sessions_logged', label: 'Sessions Logged', field: 'sessions_logged', align: 'left', sortable: true },
    { name: 'latest_submitted_time', label: 'Last Form Completed', field: 'latest_submitted_time', align: 'left', sortable: true },
    { name: 'days_since_last_completion', label: 'Days Since Last Form', field: 'days_since_last_completion', align: 'left', sortable: true },
];

export default {
    name: 'DashboardBreakdownTable',

    props: {
        rows: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        }
    },

    emits: ['select-client'],

    data() {
        return {
            columns,
            activeClientsOnly: true,
            formCompletedOnly: true,
        };
    },

    computed: {
        filteredRows() {
            let rows = this.rows;

            if (this.activeClientsOnly) {
                // Temporary clients have no Paua lifecycle status — they are
                // never filtered out by the "active clients only" toggle.
                rows = rows.filter((row) => row?.status_id === 'Active' || isTemporaryClientRow(row));
            }

            if (this.formCompletedOnly) {
                rows = rows.filter((row) => row?.sessions_logged > 0);
            }

            return rows;
        }
    },

    methods: {
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleString('en-nz');
        },

        isTemporaryClientRow(row) {
            return isTemporaryClientRow(row);
        }
    }
}
</script>

<style scoped>
.client-breakdown {
    padding: 20px;
    background:
        linear-gradient(180deg, rgba(237, 244, 239, 0.95), rgba(255, 255, 255, 1));
    border-bottom: 1px solid rgba(47, 62, 70, 0.08);
}

.client-breakdown__eyebrow {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #2f3e46;
    display: flex;
    align-items: center;
    width: 100%;
}

.client-breakdown__content {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.client-breakdown__toggles {
    display: flex;
    align-items: center;
    gap: 12px;
}

@media (max-width: 700px) {
    .client-breakdown {
        padding: 16px 12px;
    }

    .client-breakdown__toggles {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
